import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/Button";
import { api, extractError } from "../lib/api";
import {
  Hourglass,
  DownloadSimple,
  Users,
  Warning,
  CheckCircle,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

/**
 * Iter 19.52 — Super-Admin-Page: System-Einstellungen.
 * Aktuell ein Feature: Coming-Soon-Modus (Registrierung deaktiviert) + Waitlist.
 * Spätere Settings können hier einfach eingefügt werden.
 */
export default function AdminSystemSettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [registrationDisabled, setRegistrationDisabled] = useState(true);
  const [toggling, setToggling] = useState(false);

  const [waitlist, setWaitlist] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/admin/system-settings");
        const flag = data?.registration_disabled;
        setRegistrationDisabled(
          flag && typeof flag === "object" ? !!flag.enabled : true
        );
      } catch (err) {
        setError(extractError(err) || "Einstellungen konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setWaitlistLoading(true);
        const { data } = await api.get("/admin/waitlist");
        setWaitlist(data?.items || []);
      } catch {
        // soft fail — only the registration toggle is critical
      } finally {
        setWaitlistLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (next) => {
    setToggling(true);
    setError("");
    try {
      await api.patch("/admin/system-settings", {
        key: "registration_disabled",
        value: { enabled: next },
      });
      setRegistrationDisabled(next);
      toast.success(
        next
          ? "Coming-Soon-Modus aktiviert — Registrierung ist deaktiviert."
          : "Coming-Soon-Modus deaktiviert — Registrierung ist geöffnet."
      );
    } catch (err) {
      setError(extractError(err) || "Konnte Einstellung nicht speichern.");
    } finally {
      setToggling(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/waitlist/export.csv", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `waitlist_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(extractError(err) || "Export fehlgeschlagen.");
    } finally {
      setExporting(false);
    }
  };

  // Only super-admins should ever land here; double-check for safety.
  const isSuper = user?.role === "superadmin";

  return (
    <AdminLayout
      title="System-Einstellungen"
      subtitle="Globale Switches für die Anwendung (nur Super-Admins)."
    >
      {!isSuper && (
        <div
          role="alert"
          className="border border-status-abgelehnt-border bg-status-abgelehnt-bg p-4 rounded-sm text-sm text-status-abgelehnt-text mb-6"
        >
          <Warning size={14} weight="fill" className="inline mr-2 -mt-1" />
          Diese Seite ist nur für Super-Admins zugänglich.
        </div>
      )}

      {/* COMING SOON TOGGLE */}
      <section
        className="border border-ink-200 bg-white rounded-sm p-6 mb-6"
        data-testid="admin-system-toggle-card"
      >
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-ink-200 bg-warm-50">
            <Hourglass size={18} weight="duotone" className="text-brand-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-cabinet text-base font-semibold text-ink-900 tracking-tight">
              Coming-Soon-Modus
            </h3>
            <p className="mt-1 text-sm text-warm-700 leading-relaxed">
              Wenn aktiviert, sehen Besucher beim Klick auf <em>„Registrieren"</em>{" "}
              statt des Anmeldeformulars die Coming-Soon-Page mit der Möglichkeit,
              sich auf die Waitlist eintragen zu lassen.
            </p>
            <p className="mt-2 text-[11px] text-warm-500">
              Diese Einstellung gilt sofort für alle Besucher und bleibt nach
              Neustarts bestehen.
            </p>
          </div>
          <div className="shrink-0">
            {loading ? (
              <div className="w-32 h-10 bg-warm-100 rounded-sm loading-bar" />
            ) : (
              <ToggleSwitch
                checked={registrationDisabled}
                disabled={toggling || !isSuper}
                onChange={(v) => handleToggle(v)}
                testId="admin-coming-soon-toggle"
              />
            )}
          </div>
        </div>
        {error && (
          <div
            role="alert"
            className="mt-4 border border-status-abgelehnt-border bg-status-abgelehnt-bg px-3 py-2 rounded-sm text-sm text-status-abgelehnt-text"
          >
            <Warning size={14} weight="fill" className="inline mr-2 -mt-1" />
            {error}
          </div>
        )}
        {!loading && !error && (
          <div className="mt-4 inline-flex items-center gap-2 text-[12px] font-medium">
            {registrationDisabled ? (
              <>
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full bg-status-ausstehend-border animate-pulse"
                  aria-hidden="true"
                />
                <span className="text-status-ausstehend-text">
                  Aktiv — Registrierung deaktiviert (Coming-Soon)
                </span>
              </>
            ) : (
              <>
                <CheckCircle size={13} weight="fill" className="text-status-aktiv-text" />
                <span className="text-status-aktiv-text">
                  Inaktiv — Registrierung ist regulär geöffnet
                </span>
              </>
            )}
          </div>
        )}
      </section>

      {/* WAITLIST */}
      <section
        className="border border-ink-200 bg-white rounded-sm p-6"
        data-testid="admin-waitlist-card"
      >
        <div className="flex items-start gap-4 mb-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-ink-200 bg-warm-50">
            <Users size={18} weight="duotone" className="text-brand-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-cabinet text-base font-semibold text-ink-900 tracking-tight">
              Waitlist
            </h3>
            <p className="mt-1 text-sm text-warm-700 leading-relaxed">
              E-Mail-Adressen, die sich auf der Coming-Soon-Page eingetragen
              haben. Sobald Sie den Coming-Soon-Modus deaktivieren, können Sie
              diese Liste exportieren und per Mass-Mail benachrichtigen.
            </p>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-0.5">
              Einträge
            </div>
            <div
              className="font-cabinet text-3xl font-medium text-ink-900 tabular-nums leading-none"
              data-testid="admin-waitlist-count"
            >
              {waitlistLoading ? "—" : waitlist.length}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-5">
          <Button
            type="button"
            variant="secondary"
            disabled={exporting || waitlistLoading || waitlist.length === 0}
            onClick={exportCsv}
            data-testid="admin-waitlist-export"
          >
            <DownloadSimple size={14} weight="bold" className="mr-1.5" />
            {exporting ? "Wird exportiert…" : "Als CSV exportieren"}
          </Button>
        </div>

        {/* Compact preview list (latest 20) */}
        <div className="border border-ink-200 rounded-sm overflow-hidden">
          {waitlistLoading ? (
            <div className="px-4 py-6 text-sm text-warm-500">Wird geladen…</div>
          ) : waitlist.length === 0 ? (
            <div
              className="px-4 py-8 text-center text-sm text-warm-500"
              data-testid="admin-waitlist-empty"
            >
              Noch keine Eintragungen.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-200 bg-warm-50">
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-widest font-semibold text-warm-500">
                    E-Mail
                  </th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase tracking-widest font-semibold text-warm-500 hidden sm:table-cell">
                    Eingetragen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200" data-testid="admin-waitlist-table">
                {waitlist.slice(0, 20).map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-2.5 text-ink-900 truncate">{row.email}</td>
                    <td className="px-4 py-2.5 text-right text-warm-600 tabular-nums hidden sm:table-cell">
                      {formatDate(row.signed_up_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {waitlist.length > 20 && (
          <div className="mt-2 text-[11px] text-warm-500">
            Zeige die letzten 20 von {waitlist.length} Einträgen. Vollständige
            Liste über CSV-Export.
          </div>
        )}
      </section>
    </AdminLayout>
  );
}

function ToggleSwitch({ checked, disabled, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      disabled={disabled}
      data-testid={testId}
      className={[
        "relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2",
        checked ? "bg-brand-500" : "bg-warm-300",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200",
          checked ? "translate-x-6" : "translate-x-1",
        ].join(" ")}
      />
    </button>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-AT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
